'use client';

import { cn } from '../../../../lib/utils';
import { ReactNode } from 'react';
import { Add, ArrowDropDown } from '@crabs-ai/web-icons';
import TooltipTriggerIcon, {
  TooltipIconType,
} from '../../../primitives/feedback-and-status/tooltip/TooltipTriggerIcon';
import { TooltipProps } from '../../../primitives/feedback-and-status/tooltip/Tooltip';
import {
  RadixTooltip,
  RadixTooltipContent,
  RadixTooltipProvider,
  RadixTooltipTrigger,
  TooltipText,
} from '../../../../components/ui/tooltip';
import {
  getHeaderSizeStyles,
  getHeaderPaddingClass,
  type HeaderSize,
} from '../utils/header-styles';

export interface ControlsHeaderProps {
  className?: string;
  /** 제목 */
  title: string;
  /** Leading 아이콘 */
  leadingIcon?: ReactNode;
  /** Leading 아이콘 표시 여부 */
  isShowLeadingNode?: boolean;
  /** Tooltip 아이콘 표시 여부 */
  isShowTooltipIcon?: boolean;
  /** Tooltip 아이콘 타입 */
  tooltipIconType?: TooltipIconType;
  /** Tooltip 텍스트 */
  tooltipText?: string;
  /** 추가 Tooltip Props */
  tooltipProps?: Partial<Omit<TooltipProps, 'textNode' | 'children'>>;
  /** 활성화 상태 여부 */
  isActive?: boolean;
  /** Inactive 상태에서 trailing에 표시할 액션 노드 (예: "+" 버튼) */
  actionNode?: ReactNode;
  /** Inactive 상태 클릭 핸들러 (활성화 트리거) */
  onAction?: () => void;
  /** Active 상태에서 arrow 좌측에 표시할 trailing 액션 노드 (예: delete 버튼) */
  activeTrailingNode?: ReactNode;
  /** Active 상태의 열림/닫힘 (arrow 회전용) */
  isOpened?: boolean;
  /** Active 상태의 클릭 핸들러 (접기/펼치기) */
  onClick?: () => void;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 반응형 여부 */
  responsive?: boolean;
  /** 사이즈 */
  size?: HeaderSize;
  /** Inactive 상태 액션(+) 아이콘 툴팁 텍스트 */
  actionTooltip?: string;
}

export default function ControlsHeader({
  className = '',
  title,
  leadingIcon,
  isShowLeadingNode = false,
  isShowTooltipIcon = false,
  tooltipIconType = 'help',
  tooltipText,
  tooltipProps,
  isActive = false,
  actionNode,
  onAction,
  activeTrailingNode,
  isOpened = false,
  onClick,
  disabled = false,
  responsive = false,
  size = 'lg',
  actionTooltip,
}: ControlsHeaderProps) {
  const currentSize = getHeaderSizeStyles(size);
  const paddingClass = getHeaderPaddingClass(responsive);

  const arrowSizeClass = { sm: 'size-4', md: 'size-5', lg: 'size-6' }[size];

  const titleColor = disabled
    ? 'text-sys-cont-neutral-disabled-default'
    : isActive
      ? 'text-sys-cont-neutral-default'
      : 'text-sys-cont-neutral-lightest';

  const iconFill = disabled
    ? '[&_path]:fill-sys-cont-neutral-disabled-default'
    : isActive
      ? '[&_path]:fill-sys-cont-neutral-default'
      : '[&_path]:fill-sys-cont-neutral-lightest';

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={isActive ? onClick : onAction}
      className={cn(
        'controls-header',
        'w-full rounded-xs bg-sys-bg-surface py-2xs',
        paddingClass,
        'flex items-center justify-between gap-2xs',
        'transition-colors duration-200',
        !disabled && 'cursor-pointer hover:bg-sys-bg-surface-hover',
        !disabled && 'active:bg-sys-bg-surface-press',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <div className="flex items-center gap-4xs">
        {isShowLeadingNode && leadingIcon && (
          <div
            className={cn(
              'flex items-center transition-[fill] duration-200',
              iconFill,
              currentSize.icon,
            )}
          >
            {leadingIcon}
          </div>
        )}
        <h3
          className={cn(
            'flex items-center transition-colors duration-200',
            currentSize.title,
            titleColor,
          )}
        >
          {title}
        </h3>
        {isShowTooltipIcon && tooltipText && (
          <TooltipTriggerIcon
            iconType={tooltipIconType}
            tooltipText={tooltipText}
            iconColorClassName={iconFill}
            isNonInteractiveTrigger
            tooltipProps={tooltipProps}
          />
        )}
      </div>

      {/* Trailing 영역 */}
      {isActive ? (
        <div className="flex items-center gap-2xs">
          {activeTrailingNode && (
            <div
              role="presentation"
              onClick={(e) => e.stopPropagation()}
            >
              {activeTrailingNode}
            </div>
          )}
          <ArrowDropDown
            className={cn(
              arrowSizeClass,
              'transition-transform duration-200',
              isOpened && 'rotate-180',
              '[&_path]:fill-sys-cont-neutral-default',
            )}
          />
        </div>
      ) : actionTooltip ? (
        <RadixTooltipProvider delayDuration={100}>
          <RadixTooltip>
            <RadixTooltipTrigger asChild>
              <div
                className={cn(
                  'flex items-center [&_path]:fill-sys-cont-neutral-default',
                  arrowSizeClass,
                )}
              >
                {actionNode ?? <Add />}
              </div>
            </RadixTooltipTrigger>
            <RadixTooltipContent side="top">
              <TooltipText>{actionTooltip}</TooltipText>
            </RadixTooltipContent>
          </RadixTooltip>
        </RadixTooltipProvider>
      ) : (
        <div
          className={cn(
            'flex items-center [&_path]:fill-sys-cont-neutral-default',
            arrowSizeClass,
          )}
        >
          {actionNode ?? <Add />}
        </div>
      )}
    </button>
  );
}
